import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export type RollupCount = {
    openCount: number
    completedCount: number
    pendingCount?: number
    lastFetchedAt?: number
}

export type RollupCountsState = {
    users: { [userId: string]: RollupCount }
    apps: { [appId: string]: RollupCount }
    appUsers: { [key: string]: RollupCount }
    appEntitlements: { [key: string]: RollupCount }
    bulkActions: {
        users: { [userId: string]: boolean }
        apps: { [appId: string]: boolean }
        appUsers: { [key: string]: boolean }
        appEntitlements: { [key: string]: boolean }
    }
}

const initialState: RollupCountsState = {
    users: {},
    apps: {},
    appUsers: {},
    appEntitlements: {},
    bulkActions: {
        users: {},
        apps: {},
        appUsers: {},
        appEntitlements: {},
    },
}

export const optimisticRollupCounts = createSlice({
    name: 'optimisticRollupCounts',
    initialState,
    reducers: {
        setRollupCounts: (
            state,
            action: PayloadAction<{
                users?: { [userId: string]: RollupCount }
                apps?: { [appId: string]: RollupCount }
                appUsers?: { [key: string]: RollupCount }
                appEntitlements?: { [key: string]: RollupCount }
            }>,
        ): void => {
            const { users, apps, appUsers, appEntitlements } = action.payload
            const now = Date.now()
            if (users) {
                Object.entries(users).forEach(([userId, count]) => {
                    state.users[userId] = { ...count, lastFetchedAt: now }
                })
            }
            if (apps) {
                Object.entries(apps).forEach(([appId, count]) => {
                    state.apps[appId] = { ...count, lastFetchedAt: now }
                })
            }
            if (appUsers) {
                Object.entries(appUsers).forEach(([key, count]) => {
                    state.appUsers[key] = { ...count, lastFetchedAt: now }
                })
            }
            if (appEntitlements) {
                Object.entries(appEntitlements).forEach(([key, count]) => {
                    state.appEntitlements[key] = { ...count, lastFetchedAt: now }
                })
            }
        },
        incrementCompletedCount: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
            }>,
        ): void => {
            const { type, id } = action.payload

            switch (type) {
                case 'user':
                    if (state.users[id]) {
                        state.users[id] = {
                            ...state.users[id],
                            openCount: Math.max(0, state.users[id].openCount - 1),
                            completedCount: Math.max(0, state.users[id].completedCount + 1),
                        }
                    }
                    break
                case 'app':
                    if (state.apps[id]) {
                        state.apps[id] = {
                            ...state.apps[id],
                            openCount: Math.max(0, state.apps[id].openCount - 1),
                            completedCount: Math.max(0, state.apps[id].completedCount + 1),
                        }
                    }
                    break
                case 'appUser':
                    if (state.appUsers[id]) {
                        state.appUsers[id] = {
                            ...state.appUsers[id],
                            openCount: Math.max(0, state.appUsers[id].openCount - 1),
                            completedCount: Math.max(0, state.appUsers[id].completedCount + 1),
                        }
                    }
                    break
                case 'appEntitlement':
                    if (state.appEntitlements[id]) {
                        state.appEntitlements[id] = {
                            ...state.appEntitlements[id],
                            openCount: Math.max(0, state.appEntitlements[id].openCount - 1),
                            completedCount: Math.max(0, state.appEntitlements[id].completedCount + 1),
                        }
                    }
                    break
                default:
                    break
            }
        },
        decrementCompletedCount: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
            }>,
        ): void => {
            const { type, id } = action.payload

            switch (type) {
                case 'user':
                    if (state.users[id]) {
                        const current = state.users[id]
                        const newCompletedCount = Math.max(0, current.completedCount - 1)

                        // Only increment openCount if we actually decremented completedCount
                        // This prevents openCount from growing when completedCount is already 0
                        if (newCompletedCount < current.completedCount) {
                            state.users[id] = {
                                ...current,
                                openCount: current.openCount + 1,
                                completedCount: newCompletedCount,
                            }
                        }
                        // else: No-op when completedCount is already 0
                    }
                    break
                case 'app':
                    if (state.apps[id]) {
                        const current = state.apps[id]
                        const newCompletedCount = Math.max(0, current.completedCount - 1)

                        // Only increment openCount if we actually decremented completedCount
                        // This prevents openCount from growing when completedCount is already 0
                        if (newCompletedCount < current.completedCount) {
                            state.apps[id] = {
                                ...current,
                                openCount: current.openCount + 1,
                                completedCount: newCompletedCount,
                            }
                        }
                        // else: No-op when completedCount is already 0
                    }
                    break
                case 'appUser':
                    if (state.appUsers[id]) {
                        const current = state.appUsers[id]
                        const newCompletedCount = Math.max(0, current.completedCount - 1)

                        // Only increment openCount if we actually decremented completedCount
                        // This prevents openCount from growing when completedCount is already 0
                        if (newCompletedCount < current.completedCount) {
                            state.appUsers[id] = {
                                ...current,
                                openCount: current.openCount + 1,
                                completedCount: newCompletedCount,
                            }
                        }
                        // else: No-op when completedCount is already 0
                    }
                    break
                case 'appEntitlement':
                    if (state.appEntitlements[id]) {
                        const current = state.appEntitlements[id]
                        const newCompletedCount = Math.max(0, current.completedCount - 1)

                        // Only increment openCount if we actually decremented completedCount
                        // This prevents openCount from growing when completedCount is already 0
                        if (newCompletedCount < current.completedCount) {
                            state.appEntitlements[id] = {
                                ...current,
                                openCount: current.openCount + 1,
                                completedCount: newCompletedCount,
                            }
                        }
                        // else: No-op when completedCount is already 0
                    }
                    break
                default:
                    break
            }
        },
        updateCountsByAmount: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
                amount: number
            }>,
        ): void => {
            const { type, id, amount } = action.payload

            switch (type) {
                case 'user':
                    if (state.users[id]) {
                        state.users[id] = {
                            ...state.users[id],
                            openCount: Math.max(0, state.users[id].openCount - amount),
                            completedCount: state.users[id].completedCount + amount,
                        }
                    }
                    break
                case 'app':
                    if (state.apps[id]) {
                        state.apps[id] = {
                            ...state.apps[id],
                            openCount: Math.max(0, state.apps[id].openCount - amount),
                            completedCount: state.apps[id].completedCount + amount,
                        }
                    }
                    break
                case 'appUser':
                    if (state.appUsers[id]) {
                        state.appUsers[id] = {
                            ...state.appUsers[id],
                            openCount: Math.max(0, state.appUsers[id].openCount - amount),
                            completedCount: state.appUsers[id].completedCount + amount,
                        }
                    }
                    break
                case 'appEntitlement':
                    if (state.appEntitlements[id]) {
                        state.appEntitlements[id] = {
                            ...state.appEntitlements[id],
                            openCount: Math.max(0, state.appEntitlements[id].openCount - amount),
                            completedCount: state.appEntitlements[id].completedCount + amount,
                        }
                    }
                    break
                default:
                    break
            }
        },
        decrementPendingCount: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
                amount: number
            }>,
        ): void => {
            const { type, id, amount } = action.payload

            switch (type) {
                case 'user':
                    if (state.users[id]) {
                        state.users[id] = {
                            ...state.users[id],
                            pendingCount: Math.max(0, (state.users[id].pendingCount ?? 0) - amount),
                        }
                    }
                    break
                case 'app':
                    if (state.apps[id]) {
                        state.apps[id] = {
                            ...state.apps[id],
                            pendingCount: Math.max(0, (state.apps[id].pendingCount ?? 0) - amount),
                        }
                    }
                    break
                case 'appUser':
                    if (state.appUsers[id]) {
                        state.appUsers[id] = {
                            ...state.appUsers[id],
                            pendingCount: Math.max(0, (state.appUsers[id].pendingCount ?? 0) - amount),
                        }
                    }
                    break
                case 'appEntitlement':
                    if (state.appEntitlements[id]) {
                        state.appEntitlements[id] = {
                            ...state.appEntitlements[id],
                            pendingCount: Math.max(0, (state.appEntitlements[id].pendingCount ?? 0) - amount),
                        }
                    }
                    break
                default:
                    break
            }
        },
        incrementPendingCount: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
                amount: number
            }>,
        ): void => {
            const { type, id, amount } = action.payload

            switch (type) {
                case 'user':
                    if (state.users[id]) {
                        state.users[id] = {
                            ...state.users[id],
                            pendingCount: (state.users[id].pendingCount ?? 0) + amount,
                        }
                    }
                    break
                case 'app':
                    if (state.apps[id]) {
                        state.apps[id] = {
                            ...state.apps[id],
                            pendingCount: (state.apps[id].pendingCount ?? 0) + amount,
                        }
                    }
                    break
                case 'appUser':
                    if (state.appUsers[id]) {
                        state.appUsers[id] = {
                            ...state.appUsers[id],
                            pendingCount: (state.appUsers[id].pendingCount ?? 0) + amount,
                        }
                    }
                    break
                case 'appEntitlement':
                    if (state.appEntitlements[id]) {
                        state.appEntitlements[id] = {
                            ...state.appEntitlements[id],
                            pendingCount: (state.appEntitlements[id].pendingCount ?? 0) + amount,
                        }
                    }
                    break
                default:
                    break
            }
        },
        setBulkActionInProgress: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
                inProgress: boolean
            }>,
        ): void => {
            const { type, id, inProgress } = action.payload
            switch (type) {
                case 'user':
                    state.bulkActions.users[id] = inProgress
                    break
                case 'app':
                    state.bulkActions.apps[id] = inProgress
                    break
                case 'appUser':
                    state.bulkActions.appUsers[id] = inProgress
                    break
                case 'appEntitlement':
                    state.bulkActions.appEntitlements[id] = inProgress
                    break
                default:
                    break
            }
        },
        clearRollupCounts: () => {
            return initialState
        },
        incrementOpenCount: (
            state,
            action: PayloadAction<{
                type: 'user' | 'app' | 'appUser' | 'appEntitlement'
                id: string
            }>,
        ): void => {
            const { type, id } = action.payload

            switch (type) {
                case 'user':
                    if (state.users[id]) {
                        state.users[id] = {
                            ...state.users[id],
                            openCount: state.users[id].openCount + 1,
                        }
                    }
                    break
                case 'app':
                    if (state.apps[id]) {
                        state.apps[id] = {
                            ...state.apps[id],
                            openCount: state.apps[id].openCount + 1,
                        }
                    }
                    break
                case 'appUser':
                    if (state.appUsers[id]) {
                        state.appUsers[id] = {
                            ...state.appUsers[id],
                            openCount: state.appUsers[id].openCount + 1,
                        }
                    }
                    break
                case 'appEntitlement':
                    if (state.appEntitlements[id]) {
                        state.appEntitlements[id] = {
                            ...state.appEntitlements[id],
                            openCount: state.appEntitlements[id].openCount + 1,
                        }
                    }
                    break
                default:
                    break
            }
        },
    },
})

export const {
    setRollupCounts,
    incrementCompletedCount,
    decrementCompletedCount,
    updateCountsByAmount,
    decrementPendingCount,
    incrementPendingCount,
    setBulkActionInProgress,
    clearRollupCounts,
    incrementOpenCount,
} = optimisticRollupCounts.actions
